import React from 'react';
import { Container, Typography, Box, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  },
}));

const MissionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  background: '#FEFEFE',
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: '#4338CA',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const MissionGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(6),
  },
}));

const StorySection = styled(Box)(({ theme }) => ({
  background: '#FEFEFE',
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  padding: theme.spacing(6),
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
}));

const About = () => {
  const { t } = useTranslation();
  
  // Set page title for analytics
  usePageTitle('About Us');

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h1" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: 3,
            }}
          >
            {t('about.title')}
          </Typography>
          <Typography 
            variant="h2" 
            align="center" 
            paragraph
            sx={{
              color: '#6B7280',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: 1.6,
              maxWidth: '800px',
              margin: '0 auto',
            }}
          >
            {t('about.subtitle')}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <StorySection role="region" aria-labelledby="story-heading">
            <Typography 
              id="story-heading"
              variant="h2" 
              component="h2" 
              align="center" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              {t('about.story.title')}
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              paragraph 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: '#6B7280',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                lineHeight: 1.7,
                marginBottom: 3,
              }}
            >
              {t('about.story.p1')}
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              paragraph 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: '#6B7280',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                lineHeight: 1.7,
              }}
            >
              {t('about.story.p2')}
            </Typography>
          </StorySection>
        </motion.div>

        <Box mt={{ xs: 8, md: 12 }} component="section" role="region" aria-labelledby="mission-heading">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              id="mission-heading"
              variant="h2" 
              component="h2" 
              align="center" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#111827',
                marginBottom: 2,
              }}
            >
              {t('about.mission.title')}
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              paragraph 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto', 
                mb: { xs: 4, md: 6 },
                color: '#6B7280',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                lineHeight: 1.7,
              }}
            >
              {t('about.mission.description')}
            </Typography>
          </motion.div>

          <MissionGrid>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <MissionCard>
                <Typography 
                  variant="h3" 
                  component="h3" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    marginBottom: 3,
                  }}
                >
                  {t('about.mission.cards.democratize.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('about.mission.cards.democratize.description')}
                </Typography>
              </MissionCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <MissionCard>
                <Typography 
                  variant="h3" 
                  component="h3" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    marginBottom: 3,
                  }}
                >
                  {t('about.mission.cards.innovate.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('about.mission.cards.innovate.description')}
                </Typography>
              </MissionCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <MissionCard>
                <Typography 
                  variant="h3" 
                  component="h3" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    marginBottom: 3,
                  }}
                >
                  {t('about.mission.cards.empower.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('about.mission.cards.empower.description')}
                </Typography>
              </MissionCard>
            </motion.div>
          </MissionGrid>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <Paper 
            sx={{
              p: { xs: 3, sm: 6, md: 8 }, 
              mt: { xs: 8, md: 12 }, 
              textAlign: 'center',
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
              border: '1px solid rgba(94, 106, 210, 0.1)',
            }}
          >
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#111827',
                marginBottom: 2,
              }}
            >
              {t('about.cta.title')}
            </Typography>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ 
                maxWidth: '600px', 
                mx: 'auto', 
                mb: 4,
                color: '#6B7280',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                lineHeight: 1.7,
              }}
            >
              {t('about.cta.description')}
            </Typography>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/upload"
                sx={{ 
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4C5BC7 0%, #7C3AED 100%)',
                  }
                }}
              >
                {t('about.cta.button')}
              </Button>
            </motion.div>
          </Paper>
        </motion.div>
      </Container>
    </PageContainer>
  );
};

export default About;